import React, { DragEvent, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectSelectedImage,
    selectImageProject,
    selectSelectedSlotIndex,
} from '../../../../store/selectors/imageSelectors';
import {
    setSelectedImage,
    setImageLayout,
    setSelectedSlotIndex,
} from '../../../../store/slices/imageSlice';
import s from './CurrentImages.module.css';
import IconAdd from '../../../../shared/ui/icons/IconAdd';
import { ImageAttributes } from '../../../../shared/types/imagesType';
import IconLeftArrow from '../../../../shared/ui/icons/IconLeftArrow';
import IconRightArrow from '../../../../shared/ui/icons/IconFullRightArrow';
import { changeImageOrder } from '../../../../shared/api/imageLayoutApi';
import { ImageLayoutAttributes } from '../../../../shared/types/imageLayoutTypes';
import { useAppDispatch } from '../../../../store/store';
import { swapImageOrders } from '../../../../shared/helpers/imageHelpers';

const CurrentImages: React.FC = () => {
    const dispatch = useAppDispatch();
    const selectedImage = useSelector(selectSelectedImage);
    const selectedSlotIndex = useSelector(selectSelectedSlotIndex);

    const imageProject = useSelector(selectImageProject);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const [dragOver, setDragOver] = useState<{ layoutId: string; slotIndex: number } | null>(null);
    const [draggedLayoutId, setDraggedLayoutId] = useState<string | null>(null);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const handleSelectSlot = (slotIndex: number, layoutId: string) => {
        console.log('handleSelectSlot', slotIndex, layoutId);
        dispatch(setSelectedImage({ image: null, layoutId: null }));
        dispatch(setSelectedSlotIndex({ slotIndex: slotIndex, layoutId: layoutId }));
        if (imageProject?.layouts) {
            const layoutToActivate = imageProject.layouts.find((l) => l.id === layoutId);
            if (layoutToActivate) {
                dispatch(setImageLayout(layoutToActivate));
            }
        }
    };

    const handleSelectImage = (image: ImageAttributes, layoutId: string) => {
        dispatch(setSelectedImage({ image, layoutId }));
        dispatch(setSelectedSlotIndex({ slotIndex: null, layoutId: null }));
        if (imageProject?.layouts) {
            const layoutToActivate = imageProject.layouts.find((l) => l.id === layoutId);
            if (layoutToActivate) {
                dispatch(setImageLayout(layoutToActivate));
            }
        }
    };

    const handleDragStart = (slotIndex: number, layoutId: string) => {
        setDraggedIndex(slotIndex + 1);
        setDraggedLayoutId(layoutId);
    };

    const handleDrop = (targetIndex: number, layout: ImageLayoutAttributes) => {
        dispatch(setSelectedSlotIndex({ slotIndex: null, layoutId: null }));
        if (draggedIndex === null || draggedIndex === targetIndex + 1) return;
        if (!layout.images) return;

        const from = draggedIndex;
        const to = targetIndex + 1;

        const updatedImages = swapImageOrders(layout.images, from, to);

        void dispatch(
            changeImageOrder({
                images: updatedImages
                    .filter(
                        (img) =>
                            !!img.id &&
                            imageProject?.allImagesForLayouts?.some((i) => i.id === img.id),
                    )
                    .map((img) => ({ id: img.id, order: img.meta.order })),
                layoutId: layout.id,
            }),
        );
        // dispatch(setSelectedImage({ image: null, layoutId: null }));
        setDraggedIndex(null);
        setDragOver(null);
        setDraggedLayoutId(null);
    };

    return (
        <div className={s.container}>
            {showLeftArrow && imageProject?.layouts && imageProject.layouts.length > 1 && (
                <button className={s.scrollButton} onClick={scrollLeft}>
                    <IconRightArrow />
                </button>
            )}

            <div className={s.layoutsContainer} ref={scrollContainerRef} onScroll={handleScroll}>
                {imageProject?.layouts?.map((layout, layoutIndex) => (
                    <React.Fragment key={layout.id}>
                        <div className={s.layoutItem}>
                            <div className={s.layoutContent}>
                                {Array.from({ length: layout.slot_count }).map((_, slotIndex) => {
                                    const imageInfo = layout.images?.find(
                                        (img) => img.meta.order === slotIndex + 1,
                                    );
                                    const imageId = imageInfo?.id;
                                    const image = imageProject.allImagesForLayouts.find(
                                        (img) => img.id === imageId,
                                    );

                                    return (
                                        <div
                                            key={slotIndex}
                                            className={[
                                                s.image_item,
                                                image &&
                                                selectedImage?.image?.id === image.id &&
                                                selectedImage?.layoutId === layout.id
                                                    ? s.selected
                                                    : '',
                                                !image &&
                                                selectedSlotIndex?.slotIndex === slotIndex + 1 &&
                                                selectedSlotIndex?.layoutId === layout.id
                                                    ? s.selected
                                                    : '',
                                                dragOver &&
                                                dragOver.layoutId === layout.id &&
                                                dragOver.slotIndex === slotIndex &&
                                                draggedIndex !== null
                                                    ? s.hovered
                                                    : '',
                                                draggedLayoutId !== null &&
                                                draggedLayoutId !== layout.id
                                                    ? s.disabled
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            draggable={!!image}
                                            onDragStart={() =>
                                                handleDragStart(slotIndex, layout.id)
                                            }
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOver({ layoutId: layout.id, slotIndex });
                                            }}
                                            onDragLeave={() => setDragOver(null)}
                                            onDrop={() => {
                                                handleDrop(slotIndex, layout);
                                                setDragOver(null);
                                            }}
                                            onDragEnd={() => setDraggedLayoutId(null)}
                                            onClick={() => {
                                                if (image) {
                                                    handleSelectImage(image, layout.id);
                                                } else {
                                                    handleSelectSlot(slotIndex + 1, layout.id);
                                                }
                                            }}
                                        >
                                            {image ? (
                                                <img
                                                    src={image.original_url}
                                                    alt=""
                                                    className={s.imagePreview}
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                            ) : (
                                                <IconAdd width={25} height={25} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {imageProject.layouts && imageProject.layouts.length > 1 && (
                                <div className={s.layoutNumber}>Slide #{layoutIndex + 1}</div>
                            )}
                        </div>
                        {layoutIndex < (imageProject.layouts?.length || 0) - 1 && (
                            <div className={s.layoutDivider} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {showRightArrow && imageProject?.layouts && imageProject.layouts.length > 1 && (
                <button className={s.scrollButton} onClick={scrollRight}>
                    <IconLeftArrow />
                </button>
            )}
        </div>
    );
};

export default CurrentImages;
